import { initializeSession } from '@/lib/redux/slices/researchSessionSlice';
import type { Citation } from '@/lib/utils/citationExtractor';
import type { CitationMetadataItem } from '@/types/citation-metadata';
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

// Define the state structure - citations per session
export interface CitationsState {
  citationsBySession: {
    [sessionId: string]: Citation[];
  };
  metadataBySession: {
    [sessionId: string]: CitationMetadataItem[];
  };
  sidebarViewMode: 'preview' | 'table';
  claimsSidebarViewMode: 'references' | 'claims';
  currentSessionId: string | null;
  locale: string; // Store current locale for citation extraction
  hoveredRefId: string | null; // Track currently hovered citation for bi-directional highlighting
  hoverSource: 'chat' | 'sidebar' | null; // Track where the hover originated from
  isReferenceSidebarExpanded: boolean; // Track sidebar expansion state
  referenceSidebarWidth: number; // Track sidebar width for dynamic TextPreview sizing
}

const initialState: CitationsState = {
  citationsBySession: {},
  metadataBySession: {},
  sidebarViewMode: 'preview',
  claimsSidebarViewMode: 'references',
  currentSessionId: null,
  locale: 'sk', // Default locale
  hoveredRefId: null,
  hoverSource: null,
  isReferenceSidebarExpanded: false, // Default to collapsed
  referenceSidebarWidth: 360, // Default width in pixels
};

const citationsSlice = createSlice({
  name: 'citations',
  initialState,
  reducers: {
    // Set citations for a specific session
    setCitationsForSession: (
      state,
      action: PayloadAction<{ sessionId: string; citations: Citation[] }>
    ) => {
      const { sessionId, citations } = action.payload;
      state.citationsBySession[sessionId] = citations;
    },

    // Add a single citation to a session
    addCitationToSession: (
      state,
      action: PayloadAction<{ sessionId: string; citation: Citation }>
    ) => {
      const { sessionId, citation } = action.payload;
      if (!state.citationsBySession[sessionId]) {
        state.citationsBySession[sessionId] = [];
      }
      // Check if citation already exists
      const exists = state.citationsBySession[sessionId].some(
        (c) => c.number === citation.number
      );
      if (!exists) {
        state.citationsBySession[sessionId].push(citation);
        // Keep sorted by number
        state.citationsBySession[sessionId].sort((a, b) => a.number - b.number);
      }
    },

    // Set the current active session
    setCurrentSession: (state, action: PayloadAction<string | null>) => {
      state.currentSessionId = action.payload;
    },

    // Clear citations for a specific session
    clearSessionCitations: (state, action: PayloadAction<string>) => {
      delete state.citationsBySession[action.payload];
    },

    // Clear all citations
    clearAllCitations: (state) => {
      state.citationsBySession = {};
    },

    // Set the currently hovered citation refId for bi-directional highlighting
    setHoveredRefId: (
      state,
      action: PayloadAction<{
        refId: string | null;
        source?: 'chat' | 'sidebar' | null;
      }>
    ) => {
      state.hoveredRefId = action.payload.refId;
      state.hoverSource = action.payload.source ?? null;
    },

    // Set the reference sidebar expansion state
    setReferenceSidebarExpanded: (state, action: PayloadAction<boolean>) => {
      state.isReferenceSidebarExpanded = action.payload;
    },

    // Set the reference sidebar width for dynamic TextPreview sizing
    setReferenceSidebarWidth: (state, action: PayloadAction<number>) => {
      state.referenceSidebarWidth = action.payload;
    },

    // Set the current locale for citation extraction
    setLocale: (state, action: PayloadAction<string>) => {
      state.locale = action.payload;
    },

    // Issue #1765: Set structured citation metadata for a session
    setCitationMetadataForSession: (
      state,
      action: PayloadAction<{
        sessionId: string;
        metadata: CitationMetadataItem[];
      }>
    ) => {
      const { sessionId, metadata } = action.payload;
      state.metadataBySession[sessionId] = metadata;
    },

    // Issue #1766: Toggle sidebar between preview list and metadata table
    setSidebarViewMode: (state, action: PayloadAction<'preview' | 'table'>) => {
      state.sidebarViewMode = action.payload;
    },

    // Phase 4 (FRONTEND-01): Toggle the sidebar between References and Claims
    // content — independent of setSidebarViewMode's preview/table density toggle.
    setClaimsSidebarViewMode: (
      state,
      action: PayloadAction<'references' | 'claims'>
    ) => {
      state.claimsSidebarViewMode = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Listen to researchSession actions and sync current session
    builder.addCase(initializeSession, (state, action) => {
      state.currentSessionId = action.payload.sessionId;
      state.sidebarViewMode = 'preview'; // Reset on session switch
      state.claimsSidebarViewMode = 'references'; // Reset on session switch
      // Citations are now automatically extracted by the listener middleware
      // No need to clear them here as the listener will set the correct citations
    });
  },
});

export const {
  setCitationsForSession,
  setCitationMetadataForSession,

  setCurrentSession,

  setHoveredRefId,
  setReferenceSidebarExpanded,
  setReferenceSidebarWidth,
  setLocale,
  setSidebarViewMode,
  setClaimsSidebarViewMode,
} = citationsSlice.actions;

export default citationsSlice.reducer;
